import { Box, ButtonBase, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import lauraImg from "assets/home/laura.png";
import maddyImg from "assets/home/maddy_ugc.jpg";
import taylorImg from "assets/home/taylor.png";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Plans } from "schemas/payments";
import { displayFontFamily } from "styles/theme";

import VideoDialog from "components/Styled/Video/VideoDialog";
import {
  FREE_BENTO_VIDEO_LINK,
  FREE_IMAGE_PREVIEW_LINK,
  PRO_IMAGE_PREVIEW_LINK,
  PRO_IMAGE_PREVIEW_LINK_SQUARE,
  PRO_SUBSCRIPTION_VIDEO_LINK,
  VA_IMAGE_PREVIEW_LINK,
  VA_SUBSCRIPTION_VIDEO_LINK,
} from "constants/influencer";
import { isInfluencer, isUgcCreator } from "utils/influencer";
import { trackEvent } from "utils/tracking";

import { Icon } from "../Gamification/components/shared/icons";
import { getVideosForUser } from "../TipsFromCreators";

type ResourceType = "tutorial" | "article" | "coach" | "tip";
type FilterKey = "all" | ResourceType;

const APP_ASSETS =
  "https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets";

type ResourceItem = {
  type: ResourceType;
  label: string;
  title: string;
  meta: string;
  url: string;
  thumbnail?: string;
  isVideo?: boolean;
  trackingEvent: string;
  trackingProps: Record<string, unknown>;
  inlineVideo?: {
    link: string;
    preview: string;
    widthPercentage?: number;
  };
};

const COACH_ITEMS: ResourceItem[] = [
  {
    type: "coach",
    label: "Coach",
    title: "1:1 VIP coaching with Laura",
    meta: "Rebuild your UGC strategy",
    url: "https://laurasocials.com/vipday",
    thumbnail: lauraImg,
    trackingEvent: "Partner Offers View Offer Clicked",
    trackingProps: { Offer: "Laura VIP Coaching" },
  },
  {
    type: "coach",
    label: "Coach",
    title: "Taylor's Creator Partnership Vault",
    meta: "1,300+ creator resources",
    url: "https://shop.taylorsocials.com/creator-partnership-vault/?ref=bento",
    thumbnail: taylorImg,
    trackingEvent: "Partner Offers View Offer Clicked",
    trackingProps: { Offer: "Taylor Creator Partnership Vault" },
  },
  {
    type: "coach",
    label: "Coach",
    title: "Get paid to travel with UGC (@maddyoutdoors)",
    meta: "Free training",
    url: "https://paidtotravel.buzzsprout.com/",
    thumbnail: maddyImg,
    trackingEvent: "Partner Offers View Offer Clicked",
    trackingProps: { Offer: "Maddy Travel UGC" },
  },
];

const TUTORIAL_ITEMS: ResourceItem[] = [
  {
    type: "tutorial",
    label: "Tutorial",
    title: "Getting Started with Bento",
    meta: "Learn how to pitch brands and land deals",
    url: "https://onbento.notion.site/",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "Getting Started" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Recommendations",
    meta: "Make the most of personalized recs",
    url: "https://onbento.notion.site/how-to-use-recommendations",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Recommendations" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Inbox",
    meta: "Manage brand conversations in one place",
    url: "https://onbento.notion.site/how-to-use-inbox",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Inbox" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Campaigns",
    meta: "Schedule emails in bulk effectively",
    url: "https://onbento.notion.site/how-to-use-campaigns",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Campaigns" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "Get Full Value of Pro Subscription",
    meta: "Higher limits, filters & contacts",
    url: "https://onbento.notion.site/get-full-value-pro",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Get Full Value of Pro Subscription" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "Get Full Value of Virtual Assistant",
    meta: "Combine automation with tailored pitches",
    url: "https://onbento.notion.site/get-full-value-va",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: {
      resource: "How to Get Full Value of Virtual Assistant Subscription",
    },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Your Bento Metrics",
    meta: "Improve outreach with summary metrics",
    url: "https://hatchways.notion.site/how-to-use-metrics",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Your Bento Metrics" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Get Bento Credits",
    meta: "Earn credits by referring friends",
    url: "https://onbento.notion.site/how-to-get-bento-credits",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Get Bento Credits" },
  },
];

const ARTICLE_ITEMS: ResourceItem[] = [
  {
    type: "article",
    label: "Article",
    title: "What is Pitching",
    meta: "A practical pitching guide for creators",
    url: "https://www.onbento.com/post/sales-partnerships-manual",
    thumbnail: `${APP_ASSETS}/sales-partnerships-manual.jpg`,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "What is Pitching" },
  },
  {
    type: "article",
    label: "Article",
    title: "How to Pitch",
    meta: "Approach brands that align with you",
    url: "https://www.onbento.com/post/sales-manual-start-selling",
    thumbnail: `${APP_ASSETS}/sales-manual-start-selling.jpg`,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Pitch" },
  },
  {
    type: "article",
    label: "Article",
    title: "How to Price & Negotiate",
    meta: "Price your work strategically",
    url: "https://www.onbento.com/post/sales-manual-pricing-and-contacts",
    thumbnail: `${APP_ASSETS}/sales-manual-pricing-and-contacts.jpg`,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Price & Negotiate" },
  },
  {
    type: "article",
    label: "Article",
    title: "Build a Scalable Process",
    meta: "Long-term relationships with brands",
    url: "https://www.onbento.com/post/sales-manual-building-a-sustainable-process",
    thumbnail: `${APP_ASSETS}/sales-manual-building-a-sustainable-process.jpg`,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Build a Scalable Process" },
  },
  {
    type: "article",
    label: "Article",
    title: "Prevent Spam or Blocked Emails",
    meta: "Best practices for deliverability",
    url: "https://onbento.notion.site/prevent-getting-blocked",
    thumbnail: `${APP_ASSETS}/email-blocked.jpg`,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: {
      resource: "How to Prevent Your Email from Going Into Spam",
    },
  },
  {
    type: "article",
    label: "Article",
    title: "Optimizing your pitching process",
    meta: "1K brands in 4 months, no audience",
    url: "https://www.onbento.com/post/what-i-learned-from-pitching-to-1k-brands",
    thumbnail: `${APP_ASSETS}/what-i-learned-from-pitching-to-1k-brands.jpg`,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "Optimizing your pitching process" },
  },
  {
    type: "article",
    label: "Article",
    title: "How to Close a Brand Deal",
    meta: "Turn interest into a signed deal",
    url: "https://onbento.notion.site/how-to-close-a-brand-deal?pvs=4",
    thumbnail: `${APP_ASSETS}/closing_deal.avif`,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Close a Brand Deal" },
  },
  {
    type: "article",
    label: "Article",
    title: "Write a Better Pitch Email",
    meta: "Watch · Personal pitches that convert",
    url: "https://www.loom.com/share/7e510141384149e7962f80e412882a56",
    thumbnail: `${APP_ASSETS}/write-better-email.avif`,
    isVideo: true,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to write a better Pitch Email" },
  },
  {
    type: "article",
    label: "Article",
    title: "Rewriting Creators Pitch Playlist",
    meta: "Watch · Real pitches, rewritten",
    url: "https://www.youtube.com/watch?v=KRU62IuHbaA&list=PLuAqaH-DOuVjL-wW5rEWPf3E-jYmPJ26N&index=8&ab_channel=Jaclyn",
    thumbnail: `${APP_ASSETS}/write-pitch-list.png`,
    isVideo: true,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "Rewriting Creators Pitch Playlist" },
  },
];

const TYPE_COLORS: Record<ResourceType, string> = {
  tutorial: "#C5A82A",
  article: "#4B6CB7",
  coach: "#E85D4E",
  tip: "#E4405F",
};

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "tutorial", label: "Tutorials" },
  { key: "tip", label: "Tips" },
  { key: "coach", label: "Coaches" },
  { key: "article", label: "Articles" },
];

const MOBILE_PREVIEW_COUNT = 4;

const ResourcesSection = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { subscription } = useContext(SubscriptionContext);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeVideo, setActiveVideo] = useState<{
    link: string;
    preview: string;
    widthPercentage?: number;
    title: string;
  } | null>(null);

  const isVaUser = !!currentOrg?.isVaUser;
  const isPro =
    !!subscription?.subscription?.planName &&
    !!subscription?.isSubscribed &&
    [Plans.PRO, Plans.PRO_YEARLY, Plans.PRO_QUARTERLY].includes(
      subscription.subscription.planName,
    );

  const planVideo = useMemo<ResourceItem>(() => {
    if (isVaUser) {
      return {
        type: "tutorial",
        label: "Tutorial",
        title: "Make the Most of Your Virtual Assistant Subscription",
        meta: "Watch · Plan walkthrough",
        url: "",
        thumbnail: VA_IMAGE_PREVIEW_LINK,
        isVideo: true,
        trackingEvent: "Onboarding Video Clicked",
        trackingProps: {
          title: "Make the Most of Your Virtual Assistant Subscription",
        },
        inlineVideo: {
          link: VA_SUBSCRIPTION_VIDEO_LINK,
          preview: VA_IMAGE_PREVIEW_LINK,
        },
      };
    }
    if (isPro) {
      return {
        type: "tutorial",
        label: "Tutorial",
        title: "Make the Most of Your Pro Subscription",
        meta: "Watch · Plan walkthrough",
        url: "",
        thumbnail: PRO_IMAGE_PREVIEW_LINK_SQUARE,
        isVideo: true,
        trackingEvent: "Onboarding Video Clicked",
        trackingProps: { title: "Make the Most of Your Pro Subscription" },
        inlineVideo: {
          link: PRO_SUBSCRIPTION_VIDEO_LINK,
          preview: PRO_IMAGE_PREVIEW_LINK,
          widthPercentage: 30,
        },
      };
    }
    return {
      type: "tutorial",
      label: "Tutorial",
      title: "How to Use Bento",
      meta: "Watch · Platform walkthrough",
      url: "",
      thumbnail: FREE_IMAGE_PREVIEW_LINK,
      isVideo: true,
      trackingEvent: "Onboarding Video Clicked",
      trackingProps: { title: "How to Use Bento" },
      inlineVideo: {
        link: FREE_BENTO_VIDEO_LINK,
        preview: FREE_IMAGE_PREVIEW_LINK,
      },
    };
  }, [isVaUser, isPro]);

  const tutorialItems = useMemo<ResourceItem[]>(
    () =>
      TUTORIAL_ITEMS.filter((item) => {
        if (item.title === "Get Full Value of Pro Subscription") return isPro;
        if (item.title === "Get Full Value of Virtual Assistant")
          return isVaUser;
        return true;
      }),
    [isPro, isVaUser],
  );

  const tipItems = useMemo<ResourceItem[]>(() => {
    const userIsUgc = isUgcCreator(profile);
    const userIsInfluencer = isInfluencer(profile);
    const brandDealJourney = profile?.brandDealJourney;
    return getVideosForUser(userIsUgc, userIsInfluencer, brandDealJourney).map(
      (video) => ({
        type: "tip",
        label: "Tip",
        title: video.title,
        meta: "Watch · Instagram tip",
        url: video.url,
        thumbnail: video.thumbnail,
        isVideo: true,
        trackingEvent: "Tips From Creators Video Clicked",
        trackingProps: {
          title: video.title,
          url: video.url,
          isUgcCreator: userIsUgc,
          isInfluencer: userIsInfluencer,
          brandDealJourney,
        },
      }),
    );
  }, [profile]);

  const allItems = useMemo<ResourceItem[]>(
    () => [
      planVideo,
      ...tipItems,
      ...COACH_ITEMS,
      ...ARTICLE_ITEMS,
      ...tutorialItems,
    ],
    [planVideo, tutorialItems, tipItems],
  );

  const counts = useMemo(
    () => ({
      all: allItems.length,
      tutorial: allItems.filter((i) => i.type === "tutorial").length,
      article: allItems.filter((i) => i.type === "article").length,
      coach: allItems.filter((i) => i.type === "coach").length,
      tip: allItems.filter((i) => i.type === "tip").length,
    }),
    [allItems],
  );

  const filtered = useMemo(
    () =>
      filter === "all"
        ? allItems
        : allItems.filter((item) => item.type === filter),
    [filter, allItems],
  );

  const hasMoreMobile = isSmall && filtered.length > MOBILE_PREVIEW_COUNT;
  const displayed =
    isSmall && !expanded ? filtered.slice(0, MOBILE_PREVIEW_COUNT) : filtered;

  useEffect(() => {
    setExpanded(false);
  }, [filter]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [filtered]);

  const handleClick = (item: ResourceItem) => {
    trackEvent(item.trackingEvent, item.trackingProps);
    if (item.inlineVideo) {
      setActiveVideo({ ...item.inlineVideo, title: item.title });
      return;
    }
    window.open(item.url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        mb: 2,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontFamily: displayFontFamily,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.4px",
              color: "#0E3A2A",
            }}
          >
            Resources
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <ButtonBase
                key={f.key}
                onClick={() => setFilter(f.key)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.625,
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: filter === f.key ? "#fff" : "#6B7770",
                  backgroundColor: filter === f.key ? "#0E3A2A" : "transparent",
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.625,
                  "&:hover": {
                    color: filter === f.key ? "#fff" : "#0E3A2A",
                  },
                }}
              >
                {f.label}
                <Box component="span" sx={{ opacity: 0.7, fontSize: 11 }}>
                  {counts[f.key]}
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Box>
        {isSmall
          ? hasMoreMobile && (
              <ButtonBase
                onClick={() => setExpanded((prev) => !prev)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#1F5040",
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                  "&:hover": { color: "#0E3A2A", textDecoration: "underline" },
                }}
              >
                {expanded ? "Show less" : "View all"}
              </ButtonBase>
            )
          : filtered.length > 0 && (
              <ButtonBase
                onClick={() => scrollByPage(1)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#1F5040",
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                  "&:hover": { color: "#0E3A2A", textDecoration: "underline" },
                }}
              >
                View all
                <Icon.ArrowRight size={13} />
              </ButtonBase>
            )}
      </Box>

      <Box sx={{ position: "relative" }}>
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            flexDirection: isSmall ? "column" : "row",
            flexWrap: "nowrap",
            gap: "14px",
            overflowX: isSmall ? "visible" : "auto",
            scrollSnapType: isSmall ? "none" : "x proximity",
            pb: isSmall ? 0 : 1,
            mx: isSmall ? 0 : -0.5,
            px: isSmall ? 0 : 0.5,
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(14,58,42,0.18)",
              borderRadius: 999,
            },
          }}
        >
          {displayed.map((item, idx) => (
            <ResourceTile
              key={`${item.type}-${idx}`}
              item={item}
              onClick={() => handleClick(item)}
              layout={isSmall ? "row" : "card"}
            />
          ))}
        </Box>

        {!isSmall && canScrollLeft && (
          <ScrollArrow direction={-1} onClick={() => scrollByPage(-1)} />
        )}
        {!isSmall && canScrollRight && (
          <ScrollArrow direction={1} onClick={() => scrollByPage(1)} />
        )}
      </Box>

      {activeVideo && (
        <VideoDialog
          open
          link={activeVideo.link}
          videoPreview={activeVideo.preview}
          widthPercentage={activeVideo.widthPercentage}
          title={activeVideo.title}
          handleClose={() => setActiveVideo(null)}
        />
      )}
    </Box>
  );
};

const ScrollArrow = ({
  direction,
  onClick,
}: {
  direction: 1 | -1;
  onClick: () => void;
}) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      position: "absolute",
      top: "calc(50% - 12px)",
      transform: "translateY(-50%)",
      [direction === 1 ? "right" : "left"]: -6,
      width: 36,
      height: 36,
      borderRadius: "50%",
      backgroundColor: "#fff",
      border: "1px solid #E5DCC8",
      color: "#0E3A2A",
      boxShadow: "0 2px 8px rgba(14,58,42,0.12)",
      zIndex: 1,
      "&:hover": { backgroundColor: "#F4F0E7" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        transform: direction === 1 ? "none" : "rotate(180deg)",
      }}
    >
      <Icon.ArrowRight size={18} />
    </Box>
  </ButtonBase>
);

interface TileProps {
  item: ResourceItem;
  onClick: () => void;
  layout?: "card" | "row";
}

const ResourceTile = ({ item, onClick, layout = "card" }: TileProps) => {
  const color = TYPE_COLORS[item.type];
  const hasThumb = !!item.thumbnail;
  const isRow = layout === "row";

  const thumbnail = (
    <Box
      sx={{
        aspectRatio: "16 / 10",
        width: isRow ? 116 : "auto",
        flexShrink: 0,
        backgroundColor: "#F4F0E7",
        backgroundImage: hasThumb ? `url(${item.thumbnail})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center top",
        display: "grid",
        placeItems: "center",
        position: "relative",
        color: "#6B7770",
      }}
    >
      {hasThumb ? (
        item.isVideo && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.25)",
              color: "#fff",
            }}
          >
            <Icon.Play size={isRow ? 26 : 36} />
          </Box>
        )
      ) : item.isVideo ? (
        <Icon.Play size={isRow ? 28 : 40} />
      ) : (
        <Icon.Book size={isRow ? 28 : 40} />
      )}
    </Box>
  );

  const textContent = (
    <Box
      sx={{
        p: isRow ? "10px 14px" : "12px 14px 14px",
        minWidth: 0,
        flex: isRow ? 1 : "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.625,
          color,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "currentColor",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            color: "inherit",
          }}
        >
          {item.label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          lineHeight: 1.3,
          color: "#0E3A2A",
          m: "6px 0 4px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {item.title}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          color: "#6B7770",
          display: "-webkit-box",
          WebkitLineClamp: 1,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {item.meta}
      </Typography>
    </Box>
  );

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flex: "0 0 auto",
        width: isRow ? "100%" : 248,
        scrollSnapAlign: isRow ? "none" : "start",
        display: "flex",
        flexDirection: isRow ? "row" : "column",
        alignItems: "stretch",
        textAlign: "left",
        backgroundColor: "#fff",
        border: "1px solid #E5DCC8",
        borderRadius: "14px",
        overflow: "hidden",
        transition: "transform 0.12s, box-shadow 0.12s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow:
            "0 2px 8px rgba(14,58,42,0.06), 0 1px 2px rgba(14,58,42,0.04)",
        },
      }}
    >
      {thumbnail}
      {textContent}
    </ButtonBase>
  );
};

export default ResourcesSection;
